import type { ChatParticipant } from '@/mocks/app/mockChatData';
import { nomeExibivel } from '@/utils/nomeExibivel';
import { formatPhone, normalizarPhone, normalizarPhoneBR } from '@/utils/phone';
import { motivoBloqueioAtendimentoManual } from '@/utils/plataforma';

/**
 * Identidade de um participante de grupo: o que dá para EXIBIR dele e se dá para
 * abrir uma conversa particular com ele.
 *
 * O ponto delicado é "isto é telefone?". O servidor não manda o telefone do
 * participante em campo próprio: `codigo` é o telefone OU os dígitos de um `@lid`
 * (a plataforma não revelou o número), e um contato só-LID guarda esses mesmos
 * dígitos no próprio `codigo`. Pelo formato não dá para distinguir LID de telefone
 * estrangeiro — quem diz é `isLid`, que vem da rota de participantes.
 */

/** Texto para quando a plataforma não entregou o telefone do participante. */
export const ROTULO_NUMERO_INDISPONIVEL = 'Número não disponível';

/** Dígitos de um número E.164: DDI + assinante, até 15. */
const TELEFONE_PLAUSIVEL = /^\d{8,15}$/;

/** Telefone formatado do participante, ou `null` quando a plataforma não o revelou. */
export function telefoneDoParticipante(participante: Pick<ChatParticipant, 'phone' | 'isLid'>): string | null {
    // Dígitos de LID não são número de ninguém: formatá-los seria inventar um telefone.
    if (participante.isLid) return null;
    const codigo = (participante.phone ?? '').trim();
    if (!TELEFONE_PLAUSIVEL.test(codigo)) return null;
    return formatPhone(normalizarPhone(codigo));
}

export type RotulosParticipante = {
    /** Linha de destaque: o nome, ou o telefone quando não há nome. */
    principal: string;
    /** Linha secundária: telefone ou aviso de indisponível; `null` quando a principal já é o telefone. */
    secundaria: string | null;
};

/**
 * As duas linhas de um participante na lista do grupo.
 *
 * Participante sem nome chega com o próprio código no `name`; mostrar o número em
 * cima e repeti-lo embaixo só ocupa espaço — então o telefone sobe para o destaque.
 */
export function rotulosDoParticipante(participante: Pick<ChatParticipant, 'name' | 'phone' | 'isLid'>): RotulosParticipante {
    const telefone = telefoneDoParticipante(participante);
    const nome = nomeExibivel(participante.name);

    if (nome) return { principal: nome, secundaria: telefone ?? ROTULO_NUMERO_INDISPONIVEL };
    if (telefone) return { principal: telefone, secundaria: null };
    // Nem nome nem telefone: o rótulo que o resto da tela já usa para ele (bolhas, menções).
    return { principal: participante.name, secundaria: ROTULO_NUMERO_INDISPONIVEL };
}

/** Quem enviou uma mensagem de grupo, como `MakeMessage` a descreve. */
export type RemetenteDeGrupo = {
    idContato?: string | null;
    codigo?: string | null;
};

const canonizar = (codigo: string) => (/^\d+$/.test(codigo) ? normalizarPhone(codigo) : codigo);

/**
 * Participante que enviou a mensagem. Mesma ordem de `resolvedSenderName` no
 * `ChatMessage`: por contato primeiro, depois por telefone.
 *
 * O telefone é comparado NORMALIZADO dos dois lados: o WhatsApp ainda entrega número
 * BR legado de 12 dígitos (sem o 9), e o `===` cru perderia o participante.
 */
export function localizarParticipante(
    participantes: ChatParticipant[] | null | undefined,
    remetente: RemetenteDeGrupo,
): ChatParticipant | undefined {
    if (!participantes?.length) return undefined;

    if (remetente.idContato) {
        const porContato = participantes.find(p => p.idContato === remetente.idContato);
        if (porContato) return porContato;
    }

    if (!remetente.codigo) return undefined;
    const alvo = canonizar(remetente.codigo);
    return participantes.find(p => !!p.phone && (p.phone === remetente.codigo || canonizar(p.phone) === alvo));
}

export const MOTIVOS_BLOQUEIO_RESPOSTA_PARTICULAR = {
    semRemetente: 'Não foi possível identificar quem enviou esta mensagem.',
    numeroOculto: 'O WhatsApp não revela o número deste participante, então não é possível abrir uma conversa particular com ele.',
    numeroNaoConfirmado: 'Não foi possível confirmar o número deste participante para abrir uma conversa particular.',
} as const;

/**
 * Por que não dá para responder em particular a quem enviou a mensagem; `null` quando dá.
 *
 * `participantes` `undefined` = lista ainda não carregada: a identidade não é julgada
 * aqui (a ação carrega a lista e julga no clique), só a plataforma. Uma lista
 * carregada em que o remetente NÃO aparece (saiu do grupo, ou a busca falhou) só
 * aceita o código quando ele é inequivocamente telefone — um número BR válido. Fora
 * disso, o código pode ser LID, e abrir conversa com ele mandaria a mensagem para
 * um número que não é de ninguém.
 */
export function motivoBloqueioRespostaParticular(args: {
    participantes: ChatParticipant[] | null | undefined;
    remetente: RemetenteDeGrupo;
    plataforma: string | null | undefined;
}): string | null {
    const { participantes, remetente, plataforma } = args;

    if (!remetente.idContato && !remetente.codigo) return MOTIVOS_BLOQUEIO_RESPOSTA_PARTICULAR.semRemetente;

    if (participantes) {
        const participante = localizarParticipante(participantes, remetente);
        if (participante) {
            if (!telefoneDoParticipante(participante)) return MOTIVOS_BLOQUEIO_RESPOSTA_PARTICULAR.numeroOculto;
        } else if (!remetente.codigo || !normalizarPhoneBR(remetente.codigo)) {
            return MOTIVOS_BLOQUEIO_RESPOSTA_PARTICULAR.numeroNaoConfirmado;
        }
    }

    return motivoBloqueioAtendimentoManual(plataforma);
}
